import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView, useScroll, useTransform } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Icons
import {
  Users,
  Lightbulb,
  Heart,
  Sparkles,
  Star,
  Scale,
  Globe,
  BarChart3,
  Box,
  DollarSign
} from 'lucide-react';

const AboutUs = () => {
  // Scroll animations
  const { scrollY } = useScroll();
  const parallaxY1 = useTransform(scrollY, [0, 1000], [0, -150]);
  const parallaxY2 = useTransform(scrollY, [0, 1000], [0, -100]);
  const parallaxY3 = useTransform(scrollY, [0, 2000], [0, -250]);
  const rotateParallax = useTransform(scrollY, [0, 1000], [0, 10]);
  const opacityParallax = useTransform(scrollY, [0, 300], [1, 0.5]);
  
  // Custom hook for revealing elements when they come into view
  const useRevealAnimation = (threshold = 0.2, delay = 0) => {
    const ref = useRef(null);
    const controls = useAnimation();
    const inView = useInView(ref, { threshold, triggerOnce: true });
    
    useEffect(() => {
      if (inView) {
        controls.start('visible');
      }
    }, [controls, inView]);
    
    return { ref, controls, inView };
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
  };
  
  const fadeInLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
  };
  
  const fadeInRight = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.7 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  
  const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // Section animations
  const heroSection = useRevealAnimation(0.1);
  const missionSection = useRevealAnimation(0.2);
  const storySection = useRevealAnimation(0.2);
  const valuesSection = useRevealAnimation(0.2);
  const timelineSection = useRevealAnimation(0.2);
  const ctaSection = useRevealAnimation(0.2);

  // Core values
  const coreValues = [
    {
      name: 'Authenticity',
      description: 'We believe in real voices and genuine connections that build trust.',
      icon: Star,
      color: 'from-blue-500 to-blue-600',
      lightColor: 'from-blue-400/20 to-blue-500/20'
    },
    {
      name: 'Fairness',
      description: 'We ensure both brands and influencers receive equal value and respect.',
      icon: Scale,
      color: 'from-emerald-500 to-emerald-600',
      lightColor: 'from-emerald-400/20 to-emerald-500/20'
    },
    {
      name: 'Innovation',
      description: 'We constantly evolve our platform to meet the changing digital landscape.',
      icon: Lightbulb,
      color: 'from-violet-500 to-violet-600',
      lightColor: 'from-violet-400/20 to-violet-500/20'
    },
    {
      name: 'Community',
      description: 'We foster meaningful relationships between brands and content creators.',
      icon: Users,
      color: 'from-amber-500 to-amber-600',
      lightColor: 'from-amber-400/20 to-amber-500/20'
    },
    {
      name: 'Passion',
      description: 'We are driven by our commitment to help others succeed and grow.',
      icon: Heart,
      color: 'from-rose-500 to-rose-600',
      lightColor: 'from-rose-400/20 to-rose-500/20'
    },
    {
      name: 'Excellence',
      description: 'We strive for quality in every campaign, connection, and outcome.',
      icon: Sparkles,
      color: 'from-indigo-500 to-indigo-600',
      lightColor: 'from-indigo-400/20 to-indigo-500/20'
    }
  ];
  
  // Stats section removed as we're just starting

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="About Us — Engaged Manila"
        description="Learn about Engaged Manila, the influencer marketing platform connecting brands with authentic content creators in the Philippines."
        url="/about-us"
      />
      <Header />

      {/* Hero Section - Minimalist Professional Design */}
      <section className="relative bg-white">
        {/* Top accent bar */}
        <div className="h-1 bg-primary w-full"></div>
        
        {/* Main hero content */}
        <div className="relative overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 bg-gray-50"></div>
          
          {/* Diagonal divider */}
          <div className="absolute inset-y-0 right-0 w-1/2 bg-white transform -skew-x-12 origin-top-right"></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzQjVCQTUiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptNiAwaDZ2LTZoLTZ2NnptLTEyIDBoLTZ2Nmg2di02em0tNi0xMmg2di02aC02djZ6bTEyIDB2LTZoLTZ2Nmg2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
          
          {/* Accent elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-br-full"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-tl-full"></div>
          
          {/* Blue accent line */}
          <div className="absolute left-0 top-1/2 w-full h-0.5 bg-gradient-to-r from-primary/20 via-primary/5 to-transparent transform -translate-y-1/2"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Content area - spans 7 columns on large screens */}
              <div className="lg:col-span-7">
                <motion.div 
                  ref={heroSection.ref}
                  initial="hidden"
                  animate={heroSection.controls}
                  variants={staggerContainer}
                >
                  <motion.div variants={fadeInUp}>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mr-1.5"></span>
                      Launching August 2025
                    </span>
                  </motion.div>
                  
                  <motion.h1 
                    variants={fadeInUp}
                    className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900"
                  >
                    <span className="block">About</span>
                    <span className="block text-primary">Engaged Manila</span>
                  </motion.h1>
                  
                  <motion.p 
                    variants={fadeInUp}
                    className="mt-6 text-xl leading-relaxed text-gray-600 max-w-3xl"
                  >
                    Building bridges between brands and influencers through authentic connections and meaningful collaborations.
                  </motion.p>
                  
                  <motion.div variants={fadeInUp} className="mt-10 flex flex-wrap gap-4">
                    <a 
                      href="/register" 
                      className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Join as Influencer
                    </a>
                    <a 
                      href="/register-brand" 
                      className="inline-flex items-center justify-center px-8 py-3 border border-primary text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Register Your Brand
                    </a>
                  </motion.div>
                </motion.div>
              </div>
              
              {/* Feature highlights - spans 5 columns on large screens */}
              <div className="lg:col-span-5">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  {/* Header bar */}
                  <div className="bg-primary/5 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900">Platform Highlights</h3>
                  </div>
                  
                  {/* Feature list */}
                  <div className="divide-y divide-gray-100">
                    {/* Feature 1 */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="px-6 py-4 flex items-center"
                    >
                      <div className="flex-shrink-0 mr-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-gray-900">Community-Focused</h4>
                        <p className="text-sm text-gray-500">Connect with a network of creators and brands</p>
                      </div>
                    </motion.div>
                    
                    {/* Feature 2 */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="px-6 py-4 flex items-center"
                    >
                      <div className="flex-shrink-0 mr-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Lightbulb className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-gray-900">Innovative Platform</h4>
                        <p className="text-sm text-gray-500">Modern tools for effective collaborations</p>
                      </div>
                    </motion.div>
                    
                    {/* Feature 3 */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="px-6 py-4 flex items-center"
                    >
                      <div className="flex-shrink-0 mr-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-gray-900">Fair Compensation</h4>
                        <p className="text-sm text-gray-500">Transparent pricing and payment system</p>
                      </div>
                    </motion.div>
                    
                    {/* Feature 4 */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="px-6 py-4 flex items-center"
                    >
                      <div className="flex-shrink-0 mr-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <BarChart3 className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-base font-medium text-gray-900">Detailed Analytics</h4>
                        <p className="text-sm text-gray-500">Track performance and optimize campaigns</p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              ref={missionSection.ref}
              initial="hidden"
              animate={missionSection.controls}
              variants={staggerContainer}
              className="space-y-6"
            >
              <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-gray-900 sm:text-4xl">
                Our <span className="text-primary">Mission</span>
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 leading-relaxed">
                Our platform officially began its soft launch on August 1, 2025. It was created with one mission in mind: to build a better way for brands and influencers to connect and collaborate. We saw a need for a platform that prioritizes authenticity, fairness, and measurable success, and we decided to take action.
              </motion.p>
              
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 leading-relaxed">
                In today's fast-moving digital world, influence is no longer defined by traditional advertising. It is shaped by people, stories, and real voices that connect with audiences. Brands are searching for more meaningful ways to reach their customers. Influencers are looking for opportunities to grow, earn, and be recognized for their creative work.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="pt-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-md bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">More Than a Platform</h3>
                    <p className="mt-1 text-gray-600">We are a partner in your growth, focused on creating real partnerships that benefit everyone involved.</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-blue-400/20 rounded-2xl transform -rotate-2 blur-xl"></div>
              <div className="relative">
                <div className="aspect-w-3 aspect-h-4 rounded-2xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1556155092-490a1ba16284?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80" 
                    alt="Influencer creating content" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-6">
                  <blockquote className="text-white">
                    <p className="text-lg font-medium italic">"We enable businesses to create campaigns that reach the right audiences and deliver real results."</p>
                  </blockquote>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            ref={valuesSection.ref}
            initial="hidden"
            animate={valuesSection.controls}
            variants={staggerContainer}
            className="text-center mb-20"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Core <span className="text-primary">Values</span>
            </motion.h2>
            <motion.div variants={fadeInUp} className="w-24 h-1 bg-primary/30 mx-auto mt-6"></motion.div>
          </motion.div>

          <motion.div 
            initial="hidden"
            animate={valuesSection.controls}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16"
          >
            {coreValues.map((value, index) => (
              <motion.div 
                key={value.name}
                variants={fadeInUp}
                className="group"
              >
                <div className="flex items-center mb-4">
                  <value.icon className="h-6 w-6 text-primary mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900 border-b border-primary/20 pb-1">{value.name}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-24 border-t border-gray-100 pt-12"
          >
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="max-w-lg">
                <h3 className="text-2xl font-semibold text-gray-900">Guided by our values</h3>
                <p className="mt-2 text-gray-600">Every decision we make is informed by these core principles, ensuring we deliver the best experience for both brands and influencers.</p>
              </div>
              <div className="mt-6 md:mt-0">
                <a href="/register" className="inline-flex items-center px-5 py-2.5 border border-primary text-base font-medium rounded-md text-primary hover:bg-primary/5 transition-colors duration-200">
                  Join Our Community
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white overflow-hidden relative">
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            style={{ y: parallaxY3 }}
            className="absolute -top-20 -right-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl"
          />
          <motion.div 
            style={{ y: parallaxY1 }}
            className="absolute -bottom-32 -left-20 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl"
          />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzQjVCQTUiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptNiAwaDZ2LTZoLTZ2NnptLTEyIDBoLTZ2Nmg2di02em0tNi0xMmg2di02aC02djZ6bTEyIDB2LTZoLTZ2Nmg2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            ref={storySection.ref}
            initial="hidden"
            animate={storySection.controls}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium tracking-wide mb-3">
              Our Journey
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-gray-900 sm:text-4xl md:text-5xl">
              The <span className="text-primary">Story</span> Behind Engaged Manila
            </motion.h2>
            <motion.p variants={fadeInUp} className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              How we're bridging the gap between brands and influencers
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Story content */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="space-y-6 text-lg text-gray-600">
                <div className="relative pl-10 border-l-2 border-primary/20 py-2">
                  <div className="absolute left-[-8px] top-0 h-4 w-4 rounded-full bg-primary"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">The Beginning</h3>
                  <p>
                    The idea behind this platform started earlier in 2025. At the time, we were working closely with small businesses and content creators. What we heard again and again was frustration.
                  </p>
                </div>
                
                <div className="relative pl-10 border-l-2 border-primary/20 py-2">
                  <div className="absolute left-[-8px] top-0 h-4 w-4 rounded-full bg-primary"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">The Challenge</h3>
                  <p>
                    Business owners told us they wanted to work with influencers but had no idea how to start. They felt limited by budget, knowledge, or lack of access. On the other hand, influencers shared that they had the creativity and reach, but not enough opportunities or fair compensation.
                  </p>
                </div>
                
                <div className="relative pl-10 border-l-2 border-primary/20 py-2">
                  <div className="absolute left-[-8px] top-0 h-4 w-4 rounded-full bg-primary"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">The Solution</h3>
                  <p>
                    There was clearly a disconnect. The potential was there, but the tools and structure were missing. So we asked ourselves a question: What if there was a platform that made it easy, fair, and rewarding for both sides to work together?
                  </p>
                </div>
                
                <div className="relative pl-10 border-l-2 border-primary/20 py-2">
                  <div className="absolute left-[-8px] top-0 h-4 w-4 rounded-full bg-primary"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Our Vision</h3>
                  <p>
                    We started with a clear vision. We wanted to create a space where brands of any size could find the right voices to represent them. At the same time, we wanted influencers to be recognized and rewarded for their impact, with the option to earn either money or products based on the campaign type.
                  </p>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-primary/5 to-blue-400/5 rounded-xl border border-primary/10">
                <blockquote className="italic text-gray-700">
                  "We do not aim to be the biggest overnight. We aim to be the most trusted. Every campaign we support is designed to deliver value, not just exposure. Every creator we work with is treated as a professional."
                </blockquote>
                <p className="mt-4 font-medium text-primary">— Engaged Manila Founding Team</p>
              </div>
            </motion.div>
            
            {/* Story image */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-blue-400/20 rounded-2xl transform rotate-3 blur-xl"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1742&q=80" 
                    alt="Team collaboration" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                    <div className="p-6">
                      <span className="bg-primary/90 text-white px-3 py-1 rounded-full text-sm font-medium">August 2025</span>
                      <h3 className="text-xl font-bold text-white mt-2">Platform Launch</h3>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-lg shadow-xl border border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span className="font-medium">Starting Soon</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Official launch August 2025</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            ref={timelineSection.ref}
            initial="hidden"
            animate={timelineSection.controls}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium tracking-wide mb-3">
              Our Progress
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-gray-900 sm:text-4xl md:text-5xl">
              The <span className="text-primary">Journey</span> So Far
            </motion.h2>
            <motion.p variants={fadeInUp} className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              Key milestones in our growth story.
            </motion.p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Vertical line */}
            <motion.div 
              initial={{ height: 0 }}
              whileInView={{ height: '100%' }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-primary/80 via-primary/50 to-primary/20"
            ></motion.div>

            {/* Timeline items */}
            <div className="space-y-24">
              <motion.div 
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative"
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4 z-10">
                  <div className="h-10 w-10 rounded-full border-4 border-primary bg-white shadow-lg flex items-center justify-center">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="ml-auto mr-8 md:mr-0 md:w-5/12 md:pr-12 text-right">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-xl font-bold text-gray-900">Concept Development</h3>
                    <time className="text-sm text-primary font-semibold block mb-3">April 2025</time>
                    <p className="text-gray-600">Initial idea formation and market research for a platform connecting brands and influencers with a focus on authentic reviews.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4 z-10">
                  <div className="h-10 w-10 rounded-full border-4 border-primary bg-white shadow-lg flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="mr-auto ml-8 md:ml-0 md:w-5/12 md:pl-12 text-left">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-xl font-bold text-gray-900">Platform Launch</h3>
                    <time className="text-sm text-primary font-semibold block mb-3">August 2025</time>
                    <p className="text-gray-600">Official launch of Engaged Manila, connecting brands with influencers across the Philippines.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="relative"
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-4 z-10">
                  <div className="h-10 w-10 rounded-full border-4 border-primary bg-white shadow-lg flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="ml-auto mr-8 md:mr-0 md:w-5/12 md:pr-12 text-right">
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                    <h3 className="text-xl font-bold text-gray-900">Growing Community</h3>
                    <time className="text-sm text-primary font-semibold block mb-3">Present</time>
                    <p className="text-gray-600">Expanding our network of influencers and brands, with a focus on authentic connections and meaningful campaigns.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Join Our Story
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-blue-100">
              Be part of the future of influencer marketing
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <a
                href="/register"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-primary bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
              >
                Register as Influencer
              </a>
              <a
                href="/register-brand"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-700"
              >
                Register as Brand
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
